import { useMemo } from 'react';
import { InternalApiEntityType, InternalApiCustomListSignificantFields } from '../../api-internal';
import { useQueryCustomListSignificantFields } from './customListSuiActions';
import { useMe } from '../../organisation/useMe';
import { isFeatureEnabled, ORGANISATION_FEATURE } from '../../organisation/feature/helper';

export const hasListMissingSui = (
  listId: string,
  personFields?: InternalApiCustomListSignificantFields,
  transactionFields?: InternalApiCustomListSignificantFields
): boolean => {
  const personConfig = personFields?._configuration.find((c) => c.listId === listId);
  const transactionConfig = transactionFields?._configuration.find((c) => c.listId === listId);
  const hasPersonFields = (personConfig?.fields?.length ?? 0) > 0;
  const hasTransactionFields = (transactionConfig?.fields?.length ?? 0) > 0;
  return !hasPersonFields || !hasTransactionFields;
};

export const useCustomListSuiWarnings = (listIds: string[]) => {
  const { features } = useMe();
  const isSuiEnabled = isFeatureEnabled(ORGANISATION_FEATURE.CUSTOM_LIST_SUI_CONFIGURATION, features);

  const { data: personFields, isLoading: isPersonLoading } = useQueryCustomListSignificantFields(
    InternalApiEntityType.PERSON,
    isSuiEnabled
  );
  const { data: transactionFields, isLoading: isTransactionLoading } = useQueryCustomListSignificantFields(
    InternalApiEntityType.TRANSACTION,
    isSuiEnabled
  );

  const isLoading = isSuiEnabled && (isPersonLoading || isTransactionLoading);

  const listIdsWithMissingSui = useMemo(() => {
    if (!isSuiEnabled || !personFields || !transactionFields) {
      return new Set<string>();
    }

    const missingIds = new Set<string>();
    listIds.forEach((listId) => {
      if (hasListMissingSui(listId, personFields, transactionFields)) {
        missingIds.add(listId);
      }
    });

    return missingIds;
  }, [isSuiEnabled, personFields, transactionFields, listIds]);

  return { isSuiEnabled, isLoading, listIdsWithMissingSui };
};
